<script setup lang="ts">
/**
 * The tank panel: what you are driving and what it does.
 *
 * One tall panel down the right edge of the garage floor, in the shape a console
 * tank game uses — the vehicle's name, the player's rank bar, and the stat list
 * under it. It replaces a left-hand `.readout` column plus a *Customise hull*
 * button, and the move is not decoration: the reference layout this follows puts
 * everything numeric on the right, which is why the panel has a column of its
 * own and the dock centres under the tank rather than under the window.
 *
 * ## The slot rows are gone, and the door they carried is not
 *
 * It used to draw three rows of square slots — the eight hull-part fittings, four
 * owned liveries, the two timed boosts — copied from a console garage's AMMO /
 * EQUIPMENT / CAMOUFLAGE / BOOSTERS strip. They are cut. Every one of them was a
 * *readout of somewhere else*: the parts live in the customise window, liveries
 * on the Cosmetics tab and boosts in the shop, and none of the three rows could
 * do anything but open the screen that owns them. Twenty-four tiles at 1.4 rem
 * bought a picture of a fitting rather than the fitting, and they took the room
 * the numbers now have.
 *
 * **What survives is the one door that exists nowhere else.** `customise-open`
 * was on the first equipment tile, and the customise window is reachable from
 * this screen and from a hull card and from nowhere else at all — so the panel
 * keeps a single button carrying that hook. `garage-hangar` asserts the lobby
 * still offers a way in, rightly: losing it would strand the eight part ladders
 * behind no door. Liveries and boosts need no replacement, because both are
 * tabs in the title bar and a tab is the front door players already know.
 *
 * ## Where the numbers come from
 *
 * The stats are the lobby's, resolved once and passed in — this panel formats
 * nothing and computes nothing about a tank. Each row carries a glyph, drawn in
 * `StatIcon.vue` for the reason every mark in this game is drawn rather than
 * fetched, and switched on with `StatList`'s `icons` prop so the shop's own copy
 * of the same list is untouched.
 *
 * The rank bar is the player's, not the tank's. The reference's ELITE bar is a
 * per-vehicle grind this game does not have — an item has an upgrade level, but
 * that is a number you buy rather than one you earn, and a bar you can fill with
 * credits says nothing about how far anybody has come. Rank is the thing that
 * earns, and `xpIntoRank` / `xpForNextRank` are already on the wallet for the
 * ladder window, so the bar restates nothing and computes nothing: **the curve
 * stays server-side** and this does one division.
 *
 * ## Compact: a phone keeps the door and loses the numbers
 *
 * On `compact` — the garage's own `(max-height: 520px)`, resolved once in
 * `GarageScreen` and handed down — this panel is **the tank's name and one
 * button**, and everything numeric goes. That is not a shrink: the rank bar and
 * the eleven stats are simply not rendered.
 *
 * Measured on the tab this sits on, at 667x375: the panel's own box came out
 * **384x2 px** and its stat list tried to scroll **536 px of content inside a
 * 19 px box**, with the Customize button at y=349 on a 375 px screen — the one
 * door on the panel, ten pixels below the fold. There is no height at which
 * eleven stats and a 375 px viewport are both true, so the fight is not worth
 * having: **stats are compared on the hulls tab**, where the cards carry the
 * delta against what is fitted and where somebody comparing them already is.
 *
 * What may not go is `customise-open`. The customise window is reachable from
 * this screen and from a hull card and from nowhere else at all, so dropping the
 * panel outright would strand the eight part ladders behind no door on a phone.
 * Keeping the panel as a **name plate with that one button on it** is why this is
 * a prop rather than a `v-if` in the lobby: one component owns the hook, so there
 * is no second copy of it in the DOM to drift, and `garage-hangar` keeps finding
 * it at every viewport.
 *
 * The name stays for the same reason it is set large here: with no sidebar and no
 * stat list, a phone player has nothing else on screen that says what they are
 * looking at. The rank goes, because the title bar's rank chip already carries
 * the rank and its insignia on every tab.
 */
import { computed, ref } from 'vue';

import StatList from '@/garage/StatList.vue';
import type { StatDescriptor } from '@/garage/stat-display';
import { useGarage } from '@/state/garage';

const props = defineProps<{
  hullName: string | null;
  turretName: string | null;
  /** Firepower / Survivability / Mobility, as the lobby already resolved them. */
  statGroups: { id: string; label: string; stats: StatDescriptor[] }[];
  values: Record<string, number>;
  /**
   * A phone held sideways, resolved by `GarageScreen`'s `matchMedia` and passed
   * down rather than re-asked here.
   *
   * One source of truth per screen, the arrangement `showSidebar` uses one file
   * up: a second `matchMedia` in this component would be a second thing that can
   * disagree with the `.garage.compact` class every other rule keys off, and the
   * two would disagree for exactly one frame after a rotation.
   */
  compact?: boolean;
}>();

const emit = defineEmits<{
  /** Open the customise window on this hull. */
  customise: [hullId: string];
}>();

const garage = useGarage();

const wallet = computed(() => garage.wallet.value);
const hullId = computed(() => garage.equippedHull.value?.id ?? null);

/** How far through the current rank, 0–1. Null at the top of the ladder. */
const rankProgress = computed(() => {
  const w = wallet.value;
  if (!w || w.xpForNextRank === null) return null;
  // `xpForNextRank` is documented as "XP the current rank needs in total" — the
  // span of this rung, not a cumulative target. Treating it as cumulative and
  // subtracting the rank's start produced a negative denominator and a label
  // reading `2,965 / -25,101 XP` on the first screenshot.
  const span = w.xpForNextRank;
  return span > 0 ? Math.min(1, Math.max(0, w.xpIntoRank / span)) : 0;
});

const numberFormat = new Intl.NumberFormat('en-GB');
const fmt = (n: number): string => numberFormat.format(Math.round(n));

/**
 * Whether the stat list is folded away, leaving only the name and the rank bar.
 *
 * A browser preference, not an account fact — same shape as the map and mode
 * choices in `GarageScreen.vue` — because `GarageLobby` (and this panel with
 * it) unmounts on every tab change, so a plain `ref` would forget the choice
 * the moment somebody looked at the shop and came back.
 */
const STATS_COLLAPSED_KEY = 'sow.tankPanelStatsCollapsed';

function readStoredCollapsed(): boolean {
  try {
    // Collapsed unless the player explicitly opened it last time. `null`
    // (never toggled) and `'0'` (opened, then left open) are different
    // answers — reading only for `'1'` conflates them and a never-toggled
    // panel would default open regardless of which way the default should go.
    return window.localStorage.getItem(STATS_COLLAPSED_KEY) !== '0';
  } catch {
    // Storage can be denied outright. Default to the same collapsed state
    // everyone else gets.
    return true;
  }
}

const statsCollapsed = ref(readStoredCollapsed());

function toggleStats(): void {
  statsCollapsed.value = !statsCollapsed.value;
  try {
    window.localStorage.setItem(STATS_COLLAPSED_KEY, statsCollapsed.value ? '1' : '0');
  } catch {
    // See above — the toggle still works for the rest of this session.
  }
}
</script>

<template>
  <aside class="tank-panel" :class="{ compact: props.compact }" data-test="tank-panel">
    <header class="head">
      <p class="names">
        <span class="hull" data-test="lobby-hull-name">{{ props.hullName ?? 'No hull' }}</span>
        <span class="sep" aria-hidden="true">|</span>
        <span class="turret" data-test="lobby-turret-name">{{
          props.turretName ?? 'No turret'
        }}</span>
      </p>
    </header>

    <!--
      Rank is its own section, deliberately below a divider rather than folded
      into the header — a hull called "Recruit", a turret called "Recruit" and
      a rank called "Recruit" read as one repeated word when they share a
      block. See the file header.
    -->
    <div v-if="wallet && !props.compact" class="rank" data-test="panel-rank">
      <h4>Rank</h4>
      <div class="rank-line">
        <span class="rank-title">{{ wallet.rankTitle }}</span>
        <span v-if="wallet.xpForNextRank !== null" class="rank-xp">
          {{ fmt(wallet.xpIntoRank) }} / {{ fmt(wallet.xpForNextRank) }} xp
        </span>
        <span v-else class="rank-xp top">Top rank</span>
      </div>
      <div class="track" role="presentation">
        <div class="fill" :style="{ width: `${(rankProgress ?? 1) * 100}%` }" />
      </div>
    </div>

    <template v-if="!props.compact">
      <button
        type="button"
        class="stats-toggle"
        data-test="stats-toggle"
        :aria-expanded="!statsCollapsed"
        aria-controls="tank-panel-stats"
        @click="toggleStats"
      >
        <span>Stats</span>
        <svg
          class="chevron"
          :class="{ collapsed: statsCollapsed }"
          viewBox="0 0 12 12"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M2.5 4.5 6 8l3.5-3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <div v-show="!statsCollapsed" id="tank-panel-stats" class="stats">
        <section v-for="group in props.statGroups" :key="group.id" class="stat-group">
          <h4>{{ group.label }}</h4>
          <StatList :descriptors="group.stats" :values="props.values" icons />
        </section>
      </div>
    </template>

    <div class="foot">
      <button
        type="button"
        class="customise"
        data-test="customise-open"
        :disabled="!hullId"
        :title="hullId ? 'Fit parts to this hull' : 'Fit a hull first'"
        @click="hullId && emit('customise', hullId)"
      >
        Customize hull
      </button>
    </div>
  </aside>
</template>

<style scoped>
/*
 * Dark, translucent and blurred, like every panel that floats over the hangar.
 * Opaque would be the boxed layout coming back, and the scene behind this moves
 * as the camera orbits.
 *
 * **A quarter wider than it was** (19 rem → 24 rem), with the type and the
 * padding scaled with it, because the panel is now only numbers: cutting the
 * three slot rows left the stat list as the whole subject, and a stat list is
 * the one thing here that reads better big. `DailyQuestBox` states the same
 * width — the two are one column, and a card of its own width would read as a
 * notice floating beside the panel rather than as the top of it — so the two
 * figures move together.
 */
.tank-panel {
  display: flex;
  flex-direction: column;
  /* The panel is the tallest thing on the screen and it may not push the dock
     off the bottom, so it takes the height it is given and no more. */
  min-height: 0;
  max-height: 100%;
  width: 24rem;
  max-width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 4px;
  background: rgba(10, 13, 17, 0.66);
  backdrop-filter: blur(9px);
  pointer-events: auto;
}

.head {
  flex: none;
  padding: 0.75rem 0.95rem 0.7rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

/*
 * One line: `$hull | $turret`, not a name stacked over a subtitle.
 *
 * They used to be two lines at very different weights (1.55rem white versus
 * 0.78rem accent) with the rank directly beneath — so a Recruit hull on a
 * Recruit turret at Recruit rank read as the word repeated three times with no
 * visual reason to tell the three apart. Inline with a rule between them says
 * "these are one loadout, named in two parts" the way a slash in a file path
 * does; the rank moved out to its own section below (see `.rank`) so it stops
 * reading as a fourth repetition of the same word.
 */
.names {
  margin: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.4rem;
  min-width: 0;
}

/*
 * The display face, and `font-weight: 400` is load-bearing.
 *
 * Protest Guerrilla ships exactly one weight, and a browser asked for a bolder
 * one synthesises it — which on a face this heavy thickens the strokes into the
 * counters and closes them. It reads as a slightly muddy heading rather than as
 * a bug. Same rule as `style.css`'s own heading block.
 */
.names .hull {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 1.28rem;
  line-height: 1.05;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  color: #fff;
  /* A twenty-character hull name is a real thing in this catalogue, and a panel
     that widened for one would move the tank. It wraps instead. */
  overflow-wrap: anywhere;
}

.names .sep {
  color: var(--steel-400);
  font-size: 1.05rem;
  line-height: 1;
}

/*
 * The body face rather than the display one — two names in the same heavy
 * stencil face would read as one word, which is the very thing this line
 * exists to stop. Sized and weighted close to the hull's own rather than
 * treated as a subtitle, per the file header: "similar in size and weight",
 * told apart by the accent colour and the font change alone.
 */
.names .turret {
  font-family: var(--font-body);
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--accent);
  overflow-wrap: anywhere;
}

/*
 * Its own section, below the header's own divider — see `.names` above for
 * why. A small uppercase label matches every other section header on this
 * panel (`.stat-group h4`, the stats toggle), so "Rank" reads as a heading
 * rather than as another line of the tank's name.
 */
.rank {
  padding: 0.6rem 0.95rem 0.7rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.rank h4 {
  margin: 0 0 0.35rem;
  color: var(--accent);
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.rank-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.rank-title {
  color: var(--accent);
  font-weight: 700;
}

.rank-xp {
  color: rgba(255, 255, 255, 0.45);
  font-variant-numeric: tabular-nums;
}

.rank-xp.top {
  color: var(--accent);
}

.track {
  margin-top: 0.3rem;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.fill {
  height: 100%;
  background: var(--accent);
}

/*
 * The row that folds the stat list away.
 *
 * A full-width button rather than a small corner icon, because the label and
 * the chevron are one control — pressing the word "Stats" should do the same
 * thing as pressing the arrow beside it. It sits where the group headers
 * inside `.stats` do (same accent, same letter-spacing) so it reads as a
 * section header first and a button second.
 */
.stats-toggle {
  flex: none;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.55rem 0.95rem;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  background: none;
  color: var(--accent);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.stats-toggle:hover {
  background: rgba(255, 255, 255, 0.03);
}

.chevron {
  width: 0.7rem;
  height: 0.7rem;
  flex: none;
  transition: transform 140ms ease-out;
}

.chevron.collapsed {
  transform: rotate(-90deg);
}

@media (prefers-reduced-motion: reduce) {
  .chevron {
    transition: none;
  }
}

/* The only scrolling region in the panel. See the comment on the markup. */
.stats {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0.7rem 0.95rem 0.5rem;
}

.stat-group + .stat-group {
  margin-top: 0.7rem;
}

.stat-group h4 {
  margin: 0 0 0.3rem;
  color: var(--accent);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

/*
 * The stat list one size up, and only here.
 *
 * `StatList` is the readout on five screens and its own sizes are tuned for a
 * card in a grid; this panel is a quarter wider than it was and is nothing but
 * that list, so the rows are scaled to it from the outside rather than by
 * changing a shared component under four other callers.
 */
.stats :deep(.stats) {
  gap: 0.85rem;
}

/*
 * More contrast than `StatList`'s own default. That component's `--steel-400`
 * is tuned for a label sitting under a bright card in a lit grid; here it sits
 * on a dark, blurred panel over the hangar, where the same colour reads as
 * barely-there. Scoped to this panel rather than raised in `StatList` itself,
 * which is the readout on four other screens with their own backgrounds.
 */
.stats :deep(dt) {
  font-size: 0.78rem;
  color: var(--steel-200, #b7c3d1);
}

.stats :deep(dd) {
  font-size: 0.94rem;
}

.stats :deep(.track) {
  height: 5px;
}

.foot {
  flex: none;
  padding: 0.6rem 0.95rem 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

/*
 * Quiet, not accent. The accent slab on this screen is the way out of it — the
 * In-battle button — and a second one in the corner would compete with it for
 * the same glance.
 */
.customise {
  width: 100%;
  padding: 0.55rem 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.04);
  color: var(--steel-200, #d7dde3);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.customise:not(:disabled):hover {
  border-color: var(--accent);
  color: var(--accent);
}

.customise:disabled {
  opacity: 0.45;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Compact: a name plate with one door on it.
 *
 * Every rule here is a *shrink* of something above; the two things that actually
 * go — the rank bar and the stat list — go in the markup, because a panel this
 * small must not be laying out a stat list it does not draw. See the header.
 *
 * 13 rem rather than 24, and it is a `width` for the reason the desktop figure is
 * one: a shrink-to-fit plate would change width whenever the hull did, which on
 * the one screen whose subject is the tank means the corner moves every time the
 * player fits something. 13 rem is 208 px, which holds the catalogue's longest
 * hull names — `Juggernaut` and `Cinderhulk`, ten characters — on one line at the
 * size below, and leaves the daily-quest card its own corner on a 667 px row:
 * 208 px of plate plus 248 px of card plus the gutters is 470 px of 639. Anything
 * longer wraps rather than widening, which is `overflow-wrap: anywhere` above
 * doing the job it was put there for.
 * ═══════════════════════════════════════════════════════════════════════════ */
.tank-panel.compact {
  width: 13rem;
}

.tank-panel.compact .head {
  padding: 0.5rem 0.6rem 0.45rem;
  /* Nothing under it to divide from — the stat list is gone and the foot brings
     its own rule — so the second hairline would be a line for its own sake. */
  border-bottom: none;
}

/*
 * The name, two thirds of the size and still the display face.
 *
 * `font-weight: 400` is inherited from the rule above and is as load-bearing here
 * as it is there: Protest Guerrilla ships one weight, and a synthesised bold at
 * this size closes the counters outright.
 */
.tank-panel.compact .names .hull {
  font-size: 1.05rem;
}

.tank-panel.compact .names .turret {
  font-size: 0.66rem;
  letter-spacing: 0.1em;
}

.tank-panel.compact .foot {
  padding: 0.4rem 0.6rem 0.5rem;
}

/*
 * 44 px, which is the tap-target floor `CLAUDE.md` sets project-wide.
 *
 * Measured before: 352x36 — the right width and eight pixels short on the axis a
 * thumb actually misses on. It is `min-height` rather than `height` so a wrapped
 * label grows the button instead of overflowing it.
 */
.tank-panel.compact .customise {
  min-height: 44px;
  padding: 0.5rem 0.6rem;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Small desktop: the aside costs too much of a window like 1200×719.
 *
 * A *width* query, unlike `.compact` above, which is `GarageScreen`'s own
 * `(max-height: 520px)` and answers a different question — is there room for
 * this panel at all. Below 100rem there is room, the panel is simply drawn
 * larger than the rest of the window's chrome justifies, so every rule here
 * is roughly a 10% trim of the desktop figure above it: the panel, its
 * padding and its type together, so it scales as one piece rather than
 * reflowing its own proportions. `:not(.compact)` is belt to the specificity
 * braces — `.tank-panel.compact` already outranks a single-class selector
 * however it is nested — kept explicit so the two tiers read as separate
 * decisions rather than as one rule quietly winning over another.
 * ═══════════════════════════════════════════════════════════════════════════ */
@media (max-width: 100rem) {
  .tank-panel:not(.compact) {
    width: 21.6rem;
  }

  .tank-panel:not(.compact) .head {
    padding: 0.68rem 0.85rem 0.63rem;
  }

  .tank-panel:not(.compact) .names .hull {
    font-size: 1.15rem;
  }

  .tank-panel:not(.compact) .names .turret {
    font-size: 1rem;
  }

  .tank-panel:not(.compact) .rank {
    padding: 0.54rem 0.85rem 0.63rem;
  }

  .tank-panel:not(.compact) .rank h4 {
    font-size: 0.58rem;
  }

  .tank-panel:not(.compact) .rank-line {
    font-size: 0.65rem;
  }

  .tank-panel:not(.compact) .stats-toggle {
    padding: 0.5rem 0.85rem;
    font-size: 0.65rem;
  }

  .tank-panel:not(.compact) .stats {
    padding: 0.63rem 0.85rem 0.45rem;
  }

  .tank-panel:not(.compact) .stat-group h4 {
    font-size: 0.65rem;
  }

  .tank-panel:not(.compact) .stats :deep(dt) {
    font-size: 0.7rem;
  }

  .tank-panel:not(.compact) .stats :deep(dd) {
    font-size: 0.85rem;
  }

  .tank-panel:not(.compact) .foot {
    padding: 0.54rem 0.85rem 0.68rem;
  }

  .tank-panel:not(.compact) .customise {
    padding: 0.5rem 0.63rem;
    font-size: 0.7rem;
  }
}
</style>
